import imageCompression from 'browser-image-compression';

/**
 * Compress an image file before upload
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file) => {
  // Skip compression for files already under the target ceiling — no point
  // re-encoding something that's already small enough.
  if (file.size < 500 * 1024) {
    return file;
  }

  const options = {
    maxSizeMB: 0.5,            // Max file size in MB (tightened from 1 MB in 24.0.4)
    maxWidthOrHeight: 1920,    // Max width or height
    useWebWorker: true,        // Use web worker for better performance
    fileType: file.type,       // Preserve original file type
  };

  try {
    console.log(`Compressing image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    
    const compressedFile = await imageCompression(file, options);
    
    console.log(`Compressed to: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
    console.log(`Savings: ${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%`);
    
    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    // Return original file if compression fails
    return file;
  }
};

/**
 * Validate image file before upload
 * @param {File} file - The file to validate
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateImageFile = (file) => {
  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select an image file' };
  }

  // Check file size (max 10MB before compression)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return { valid: false, error: 'Image must be under 10MB' };
  }

  return { valid: true, error: null };
};
